import { type ApproverApiModel } from '@webapp/core/data-access/api-models/ApproverApiModel'
import { mapApprover } from '@webapp/core/data-access/mappers/mapApprover'
import { mapCurrency } from '@webapp/core/data-access/mappers/mapCurrency'
import { type ApproverConstructor } from '@webapp/core/entities/internal/Approver'
import { type NextApproverChoiceConstructor } from '@webapp/modules/expense/entities/NextApproverChoice'
import { type TravelAddressConstructor } from '@webapp/modules/expense/entities/TravelAddress'
import { type TravelApprovalLogConstructor } from '@webapp/modules/expense/entities/TravelApprovalLog'
import { type TravelDestinationConstructor } from '@webapp/modules/expense/entities/TravelDestination'
import { type TravelRequestDetailConstructor } from '@webapp/modules/expense/entities/TravelRequestDetail'
import {
  type TravelDetailApprovalLogApiModel,
  type TravelDetailPermissionsApiModel,
  type TravelRequestDetailApiModel,
} from '../api-models/TravelRequestDetailApiModel'
import {
  type TravelAddressApiModel,
  type TravelDestinationApiModel,
  type TravelNextApproverChoiceApiModel,
} from '../api-models/TravelRequestSummaryApiModel'

// v1 dates are space-separated UTC strings (`2015-05-26 23:54:59+00:00`) —
// normalize to ISO so entity consumers can rely on `Date`-parseable values.
const toISODate = (value: string): ISODateString => value.replace(' ', 'T')

const mapDetailAddress = (
  address: TravelAddressApiModel
): TravelAddressConstructor => ({
  id: address.id,
  name: address.name ?? '',
  addressLineOne: address.addressLineOne ?? '',
  city: address.city ?? null,
  stateProvince: address.state_province ?? null,
  country: address.country ?? null,
  postalCode: address.postalCode ?? '',
})

const mapDetailDestination = (
  destination: TravelDestinationApiModel
): TravelDestinationConstructor => ({
  id: destination.id,
  sequence: destination.sequence,
  transport: destination.Transport,
  departurePlaceId: destination.departure,
  departureReference: destination.departureReference ?? '',
  departureDate: toISODate(destination.departureDate),
  departureAddress: mapDetailAddress(destination.departureAddress),
  arrivalPlaceId: destination.arrival,
  arrivalReference: destination.arrivalReference ?? '',
  arrivalDate: toISODate(destination.arrivalDate),
  arrivalAddress: mapDetailAddress(destination.arrivalAddress),
})

const mapDetailApprovalLog = (
  log: TravelDetailApprovalLogApiModel
): TravelApprovalLogConstructor => ({
  id: log.id,
  action: log.action,
  dateTime: toISODate(log.dateTime),
  user: {
    firstName: log.user.firstName,
    lastName: log.user.lastName,
  },
  nextApprover: log.nextApprover
    ? {
        firstName: log.nextApprover.firstName,
        lastName: log.nextApprover.lastName,
      }
    : null,
  oldTravelBudget: Number(log.oldTravelBudget),
  newTravelBudget: Number(log.newTravelBudget),
})

const mapDetailNextApproverChoice = (
  choice: TravelNextApproverChoiceApiModel
): NextApproverChoiceConstructor => ({
  id: choice.id,
  firstName: choice.firstName,
  lastName: choice.lastName,
  levelText: choice.level_text || null,
  approvalDelegatee: choice.approval_delegatee
    ? {
        id: choice.approval_delegatee.id,
        firstName: choice.approval_delegatee.firstName,
        lastName: choice.approval_delegatee.lastName,
        expectedReturnDate: choice.expected_return_date || null,
      }
    : null,
})

// A next approver's own delegate applies only when the travel was routed
// through delegation (`approval_delegatee` set); otherwise the delegation
// postdates the routing and the travel still waits on the approver.
const mapDetailNextApprover = (
  nextApprover: ApproverApiModel,
  travelApprovalDelegateeId: Nullable<number>
): ApproverConstructor => {
  const approver = mapApprover(nextApprover)
  return travelApprovalDelegateeId
    ? approver
    : { ...approver, approvalDelegatee: null }
}

export const mapTravelRequestDetailApiModel = (
  detail: TravelRequestDetailApiModel,
  permissions: TravelDetailPermissionsApiModel
): TravelRequestDetailConstructor => ({
  id: detail.id,
  uuid: detail.uuid,
  status: detail.status,
  travelBudget: Number(detail.travelBudget),
  purpose: detail.purpose,
  details: detail.details || null,
  dateCreated: toISODate(detail.dateCreated),
  dateLastModified: toISODate(detail.dateLastModified),
  canEdit: detail.can_edit,
  canDelete: detail.can_delete,
  // `data` never carries `can_approve`; it only exists in envelope metadata
  canApprove: permissions.can_approve,
  currency: mapCurrency(detail.currency),
  requester: {
    id: detail.requester.id,
    firstName: detail.requester.firstName,
    lastName: detail.requester.lastName,
    profileImage: detail.requester.profile_image || null,
  },
  nextApprover: detail.next_approver
    ? mapDetailNextApprover(detail.next_approver, detail.approval_delegatee)
    : null,
  approvalDelegateeId: detail.approval_delegatee,
  primaryCategory: detail.department.branch
    ? { id: detail.department.branch.id, name: detail.department.branch.name }
    : null,
  secondaryCategory: { id: detail.department.id, name: detail.department.name },
  destinations: detail.destinations.map(mapDetailDestination),
  logs: detail.logs.map(mapDetailApprovalLog),
  nextApproverChoices: detail.next_approver_choices.map(
    mapDetailNextApproverChoice
  ),
})
